import React, { useEffect, useRef } from 'react'

import { compressImage } from './uploadUtils.js'

const supportAppleImageFormat = (allowedExtensions) => {
  const allowedFormats = [...allowedExtensions]
  if (allowedExtensions.includes('JPG') || allowedExtensions.includes('JPEG')) {
    allowedFormats.push('HEIC', 'HEIF')
  }
  return allowedFormats
}

const uploadFile = (file, allowedExtensions, onUpload, onDrop, onDropError, t, validations, onChange, uiSchema, value, passErrorAppleImageSize) => {
  const allowedFormats = supportAppleImageFormat(allowedExtensions)
  const constraints = validations.reduce((acc, validation) => ({
    ...acc,
    [validation.validationType]: validation
  }), {})
  const KB = 1024

  const extRegex = new RegExp(`(${allowedFormats.join('|')})$`, 'i')
  if (!extRegex.test(file.name)) {
    onDropError(t('GallerySelectorDuc.Error.UnsupportedFileTypes'))
    return
  }

  if (/hei[cf]$/i.test(file.name)) {
    if (file.size > constraints.ImageMaxSize.parameters.maxSizeMB * KB * KB) {
      passErrorAppleImageSize()
    } else {
      compressImage({ file, maxSizeMB: 5, qualityStep: 0.1, currentQuality: 0.9 })
        .then((resultBlob) => {
          const reader = new FileReader()
          resultBlob.name = file.name.replace(/hei[cf]$/i, 'jpg')
          reader.onloadend = () => {
            const newFile = new File([resultBlob], resultBlob.name, { type: 'image/jpeg' })
            onUpload({ imageData: reader.result, file: newFile })
            onDrop && onDrop()
          }
          reader.readAsDataURL(resultBlob)
        })
        .catch((e) => {
        console.error(e)
      })
    }
  } else {
    const reader = new FileReader()
    reader.onloadend = () => {
      onUpload({ imageData: reader.result, file: file })
      onDrop && onDrop()
    }
    reader.readAsDataURL(file)
  }
}

const simpleEventGenerator = (event, enabled) => (e) => {
  e.preventDefault()
  e.stopPropagation()
  enabled && event && event()
}

const Upload = ({
  children,
  enabled,
  clickEnabled,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDropError,
  allowedExtensions,
  onUpload,
  className,
  noDrop,
  noClick,
  t,
  clearUpload,
  validation,
  passErrorAppleImageSize,
  tabIndex
}) => {
  const formRef = useRef(null)
  const uploadInput = useRef(null)
  const onDragOverHandle = simpleEventGenerator(onDragOver, enabled)
  const onDragLeaveHandle = simpleEventGenerator(onDragLeave, enabled)
  const onDragEnterHandle = simpleEventGenerator(onDragEnter, enabled)

  useEffect(() => {

    if (clearUpload) {
      formRef?.current?.reset()
    }
  }, [clearUpload])

  const onDropHandle = (e) => {
    if (enabled && !noDrop) {
      e.preventDefault()
      e.stopPropagation()
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        uploadFile(e.dataTransfer.files[0], allowedExtensions, onUpload, onDrop, onDropError, t, validation, passErrorAppleImageSize)
      }
    }
  }

  const onUploadChange = (e) => {
    if (enabled && !noClick) {
      if (uploadInput.current.files && uploadInput.current.files[0]) {
        uploadFile(uploadInput.current.files[0], allowedExtensions, onUpload, onDrop, onDropError, t, validation, passErrorAppleImageSize)
      }
    }
  }

  const onClickHandle = (e) => {
    if (clickEnabled) {
      uploadInput.current.click()
      e.stopPropagation()
    }
  }

  return <div onDrop={onDropHandle} onDragOver={onDragOverHandle} onDragEnter={onDragEnterHandle}
              onDragLeave={onDragLeaveHandle} onClick={onClickHandle} className={className || null}
              {...(tabIndex === '0'? {'data-xmpie-focusable':'true'}: {})}
              onKeyDown={(e) => e.key === 'Enter' && onClickHandle(e)}
              tabIndex={tabIndex}
  >
    {children}
    <form ref={formRef} className="xmp-gs-duc-upload-form"   >
      <input className="xmp-gs-duc-upload-input" type="file" accept={supportAppleImageFormat(allowedExtensions).map(e => `.${e}`).join(',')}
             ref={uploadInput}
             onChange={onUploadChange}
      />
    </form>
  </div>

}

export default Upload
